import { observer } from 'mobx-react-lite'
import { Avatar, Button, Checkbox, Scrollbar, SpinnerLoader, Typography } from 'shared/ui'
import { makeTextFieldClear, makeTextFieldIcon, TextField } from 'shared/ui/TextField'
import { getUserAvatarColor } from 'entities/Users/lib/getUserAvatarColor'
import { type IAddMemberEntry, type IAddMemberStore } from 'widgets/AddMembers/store/types'

import styles from './styles.module.scss'

export type IAddNewMembersModalContentProps<TEntry extends IAddMemberEntry = IAddMemberEntry> = {
  store: IAddMemberStore<TEntry>
}

export const AddNewMembersModalContent = observer(
  <TEntry extends IAddMemberEntry = IAddMemberEntry>({
    store,
  }: IAddNewMembersModalContentProps<TEntry>) => {
    const {
      search,
      selectedLength,
      filteredMembers,
      selectAllButtonText,
      shouldShowEmptyState,
      emptyStateMessage,
      hasNoAvailableMembers,
      loading,
      organizationMembersLoading,
      toggleSelectAll,
      selectMember,
      isMemberSelected,
      closeModalHandler,
      addNewMembers,
      setSearch,
    } = store

    return (
      <div className={styles.container}>
        <Scrollbar
          className={styles.scrollbar}
          viewClassName={styles.scrollbar__view}
          autoHeight
          autoHeightMin={0}
          autoHeightMax={'100%'}
          height={'100%'}
        >
          <div className={styles.scrollbar__content}>
            <TextField
              value={search}
              onChange={setSearch}
              InputProps={{ placeholder: 'Search...', className: styles.searchInput }}
              size='medium'
              rightActions={[
                makeTextFieldClear({
                  isButton: true,
                  isVisibleWithoutFocus: true,
                  intent: 'passive',
                }),
              ]}
              leftActions={[
                makeTextFieldIcon({
                  icon: 'search',
                  fontSize: 16,
                  color: 'var(--content-primary-tertiary)',
                }),
              ]}
              className={styles.search}
            />
            {organizationMembersLoading ? (
              <div className={styles.loadingContainer}>
                <SpinnerLoader ariaLabel={'AddNewMembersModalContent'} />
              </div>
            ) : (
              <>
                {!shouldShowEmptyState && (
                  <div className={styles.listHeader}>
                    <div className={styles.listHeaderContent}>
                      <Typography
                        ariaLabel='members-selected-title'
                        variant='text-input-label-small'
                      >
                        Members
                      </Typography>
                      <Typography
                        ariaLabel='members-selected-length'
                        variant='text-input-label-small'
                        color='var(--content-neutral-primary)'
                      >
                        ({selectedLength} selected)
                      </Typography>
                    </div>

                    <Button
                      text={selectAllButtonText}
                      typeBtn='text'
                      size='small'
                      color='var(--light-blue-60)'
                      onClick={toggleSelectAll}
                    />
                  </div>
                )}

                <div className={styles.membersList}>
                  <Scrollbar>
                    {shouldShowEmptyState ? (
                      <div className={styles.emptyState}>
                        <Typography
                          ariaLabel='empty-state-message'
                          variant='body-md-regular'
                          color='var(--content-neutral-primary)'
                          className={styles.emptyStateText}
                        >
                          {emptyStateMessage}
                        </Typography>
                      </div>
                    ) : (
                      filteredMembers.map((member) => (
                        <div
                          key={member.id}
                          className={styles.memberItem}
                          onClick={() => {
                            selectMember(member)
                          }}
                        >
                          <div className={styles.memberItemContent}>
                            <Checkbox size={16} checked={isMemberSelected(member)} />
                            <Avatar
                              size={24}
                              online={!member.isAway}
                              info={{
                                firstName: member.first_name,
                                lastName: member.last_name,
                                image: member.photo_url,
                                color: getUserAvatarColor(member.id),
                              }}
                            />
                            <Typography
                              ariaLabel='member-name'
                              variant='body-md-medium'
                              color='var(--content-primary-primary)'
                              width='170px'
                              ellipsis
                            >
                              {member.full_name}
                            </Typography>
                          </div>
                          <Typography
                            ariaLabel='member-email'
                            variant='body-xs-regular'
                            color='var(--content-neutral-primary)'
                            width='170px'
                            className={styles.memberEmailText}
                            ellipsis
                          >
                            {member.email}
                          </Typography>
                        </div>
                      ))
                    )}
                  </Scrollbar>
                </div>
              </>
            )}

            <div className={styles.actions}>
              <Button
                text='Cancel'
                contained='tertiary'
                size='medium'
                onClick={closeModalHandler}
              />
              <Button
                text={store.addButtonText}
                typeBtn='contained'
                size='medium'
                loading={loading}
                disabled={selectedLength === 0 || hasNoAvailableMembers || loading}
                onClick={addNewMembers}
              />
            </div>
          </div>
        </Scrollbar>
      </div>
    )
  }
)
